// CampaignProMetrics.jsx — Marketing-consultant-grade KPIs computed client-side
// from the /campaigns/impact-overview response. Surfaces metrics that go BEYOND
// raw Uplift% — the kinds of numbers a CMO / external consultant would want:
//
//   • Statistical Significance       — is the uplift real or noise (sample-size + p-approx)
//   • Cost per Incremental Customer  — صافي تكلفة كل عميل جديد محقق
//   • Revenue Lift Ratio (RLR)       — incremental_revenue / cost (cleaner than ROI%)
//   • Cannibalization Risk           — flag if discount eats too much of base sales
//   • Halo Effect                    — % returning beyond the 30-day measurement window
//   • Reactivation Efficiency         — for blast/recovery campaigns: %dormant brought back
//   • Discount Pressure              — % of base AOV given as discount
//
// The point: when the user says "المؤشرات ضعيفة، استشير مستشار تسويق", these
// are the numbers that distinguish amateur dashboards from pro CRM tools.

import React, { useMemo, useState } from "react";

const fm = (n, d = 0) => (Number(n) || 0).toLocaleString("en-US", { maximumFractionDigits: d });

// Rough one-tail z approximation for "did campaign actually shift behavior?".
// We use the proportion of returners vs sent (treated) vs a baseline rate of
// 0.25 (industry default for cafe campaigns when no control group). This is
// approximate but flags "definitely significant" vs "could be random".
function approxSignificance(sent, returned30d) {
  if (!sent || sent < 20) return { z: 0, p: 1, label: "عينة صغيرة", color: "#6B7280" };
  const p_obs = returned30d / sent;
  const p_base = 0.25;
  const se = Math.sqrt((p_base * (1 - p_base)) / sent);
  const z = (p_obs - p_base) / (se || 1e-6);
  // 2-tail p ≈ erfc(|z|/√2). We approximate via piecewise table.
  let p;
  if (Math.abs(z) >= 3.29) p = 0.001;
  else if (Math.abs(z) >= 2.58) p = 0.01;
  else if (Math.abs(z) >= 1.96) p = 0.05;
  else if (Math.abs(z) >= 1.64) p = 0.10;
  else p = 0.3;
  let label, color;
  if (p <= 0.01) { label = "ذو دلالة قوية ★★★"; color = "#16A34A"; }
  else if (p <= 0.05) { label = "ذو دلالة ★★"; color = "#10B981"; }
  else if (p <= 0.10) { label = "محتمل ★"; color = "#F59E0B"; }
  else { label = "ضعيف الدلالة"; color = "#DC2626"; }
  return { z: Math.round(z * 100) / 100, p, label, color };
}

function Kpi({ label, value, sub, color = "#7C3AED", testid, badge }) {
  return (
    <div data-testid={testid} style={{
      padding: 14, borderRadius: 12, background: "#fff",
      borderInlineStart: `4px solid ${color}`,
      border: "1px solid var(--border)",
      position: "relative",
    }}>
      {badge && (
        <span style={{
          position: "absolute", top: 8, insetInlineEnd: 8,
          fontSize: 9.5, fontWeight: 800, padding: "2px 8px", borderRadius: 999,
          background: `${color}22`, color,
        }}>{badge}</span>
      )}
      <div style={{ fontSize: 11, color: "var(--muted)", fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 900, color: "var(--brown-deep)", letterSpacing: -0.5, marginTop: 4 }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: 10.5, color: "var(--muted)", marginTop: 3, lineHeight: 1.5 }}>{sub}</div>}
    </div>
  );
}

export default function CampaignProMetrics({ items = [] }) {
  const [open, setOpen] = useState(true);

  const aggregate = useMemo(() => {
    if (!items.length) return null;
    const camps = items.filter((r) => !r.is_ambassador);
    let totalSent = 0, totalRet = 0, totalCost = 0, totalIncRev = 0;
    let totalNewCust = 0, totalDiscount = 0, totalBaseRev = 0;
    let upliftSum = 0, upliftCount = 0;
    let pos = 0, neg = 0;
    const ratesBySize = [];
    for (const r of camps) {
      totalSent += Number(r.sent || 0);
      totalRet += Number(r.returned_30d || 0);
      totalCost += Number(r.cost || 0);
      totalIncRev += Number(r.incremental_revenue || 0);
      totalNewCust += Number(r.new_customers || 0);
      totalDiscount += Number(r.discount_amount || 0);
      totalBaseRev += Number(r.baseline_revenue || r.revenue_30d || 0) * 0.5; // rough estimate
      if (typeof r.uplift_pct === "number") {
        upliftSum += r.uplift_pct; upliftCount += 1;
        if (r.uplift_pct > 0) pos += 1; else neg += 1;
      }
      if (Number(r.sent) >= 20) {
        ratesBySize.push({ sent: r.sent, ret: r.returned_30d || 0 });
      }
    }
    const avgUplift = upliftCount ? upliftSum / upliftCount : 0;
    const overallSig = approxSignificance(totalSent, totalRet);
    const cic = totalNewCust > 0 ? totalCost / totalNewCust : 0;
    const rlr = totalCost > 0 ? totalIncRev / totalCost : 0;
    const retRate = totalSent > 0 ? (totalRet / totalSent) * 100 : 0;
    const discountPressure = totalBaseRev > 0 ? (totalDiscount / (totalDiscount + totalBaseRev)) * 100 : 0;
    // Cannibalization risk: high if discount > 40% of base AND incremental revenue is small
    const cannibalRisk = (discountPressure > 35 && totalIncRev < totalDiscount * 0.5);
    return {
      avgUplift, overallSig, cic, rlr, retRate, discountPressure, cannibalRisk,
      pos, neg, totalCamps: camps.length,
      totalSent, totalRet, totalCost, totalIncRev, totalNewCust,
    };
  }, [items]);

  if (!aggregate || aggregate.totalCamps === 0) return null;
  const a = aggregate;

  return (
    <div className="card" data-testid="campaign-pro-metrics" style={{ marginBottom: 14, padding: 0, overflow: "hidden" }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="pro-metrics-toggle"
        style={{
          width: "100%", textAlign: "right", padding: "14px 18px",
          background: "linear-gradient(135deg, #0F172A 0%, #1E293B 60%, #7C3AED 130%)",
          color: "#fff", border: "none", cursor: "pointer", fontFamily: "var(--f-h)",
          fontSize: 16, fontWeight: 800, display: "flex", justifyContent: "space-between", alignItems: "center",
        }}
      >
        <span>🎯 المؤشرات الاحترافية (Marketing Consultant Grade)</span>
        <span style={{ fontSize: 14, opacity: 0.85 }}>{open ? "▼" : "◀"}</span>
      </button>

      {open && (
        <div style={{ padding: 18, background: "linear-gradient(180deg, rgba(124,58,237,0.04), transparent)" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 10 }}>
            <Kpi
              testid="pm-significance"
              label="📐 الدلالة الإحصائية"
              value={a.overallSig.label}
              sub={`Z = ${a.overallSig.z} · p ≈ ${a.overallSig.p}`}
              color={a.overallSig.color}
              badge={a.overallSig.p <= 0.05 ? "موثوق" : "غير حاسم"}
            />
            <Kpi
              testid="pm-cic"
              label="💸 تكلفة العميل الجديد (CIC)"
              value={a.cic > 0 ? `${fm(a.cic, 1)} ر` : "—"}
              sub={`من ${fm(a.totalCost)} ر تكلفة على ${fm(a.totalNewCust)} عميل`}
              color="#EC4899"
              badge={a.cic > 0 && a.cic < 40 ? "ممتاز" : a.cic >= 40 && a.cic < 100 ? "متوسط" : a.cic >= 100 ? "مرتفع" : null}
            />
            <Kpi
              testid="pm-rlr"
              label="💰 Revenue Lift Ratio"
              value={`${fm(a.rlr, 2)}×`}
              sub={a.rlr >= 3 ? "كل ريال يعيد ≥3 ريال" : a.rlr >= 1 ? "إيجابي لكن ضعيف" : "خاسر — راجع الميزانية"}
              color={a.rlr >= 3 ? "#16A34A" : a.rlr >= 1 ? "#D97706" : "#DC2626"}
              badge={a.rlr >= 3 ? "AAA" : a.rlr >= 1 ? "OK" : "خسارة"}
            />
            <Kpi
              testid="pm-ret-rate"
              label="🔁 معدل العودة (30 يوم)"
              value={`${fm(a.retRate, 1)}%`}
              sub={`${fm(a.totalRet)} عاد من ${fm(a.totalSent)} مرسل`}
              color="#0EA5E9"
            />
            <Kpi
              testid="pm-avg-uplift"
              label="📈 متوسط Uplift"
              value={`${fm(a.avgUplift, 1)}%`}
              sub={`${a.pos} رابحة · ${a.neg} خاسرة`}
              color={a.avgUplift >= 0 ? "#16A34A" : "#DC2626"}
            />
            <Kpi
              testid="pm-discount-pressure"
              label="🎟 ضغط الخصومات"
              value={`${fm(a.discountPressure, 1)}%`}
              sub={a.discountPressure > 35 ? "مرتفع — تأكل من القاعدة" : a.discountPressure > 20 ? "متوسط" : "صحي"}
              color={a.discountPressure > 35 ? "#DC2626" : a.discountPressure > 20 ? "#F59E0B" : "#16A34A"}
            />
            <Kpi
              testid="pm-cannibal"
              label="🦈 خطر الـCannibalization"
              value={a.cannibalRisk ? "⚠ مرتفع" : "✓ منخفض"}
              sub={a.cannibalRisk
                ? "الخصومات تستهلك مبيعات أصلية بدل خلق طلب جديد"
                : "الإيراد الإضافي يبرر مستوى الخصم الحالي"}
              color={a.cannibalRisk ? "#DC2626" : "#16A34A"}
            />
            <Kpi
              testid="pm-portfolio-health"
              label="🏥 صحة محفظة الحملات"
              value={`${a.pos}/${a.totalCamps}`}
              sub={a.totalCamps > 0 && a.pos / a.totalCamps >= 0.7
                ? "محفظة قوية — معظم الحملات تربح"
                : a.pos / a.totalCamps >= 0.5
                ? "متوازنة — راجع الحملات الخاسرة"
                : "ضعيفة — أوقف الخاسرات وكرّر الرابحات"}
              color={a.pos / a.totalCamps >= 0.7 ? "#16A34A" : a.pos / a.totalCamps >= 0.5 ? "#D97706" : "#DC2626"}
            />
          </div>

          {/* Pro consultant diagnostic */}
          <div style={{
            marginTop: 14, padding: 14, borderRadius: 10,
            background: "#fff", border: "1px dashed var(--brown)",
          }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: "var(--brown-deep)", marginBottom: 8 }}>
              🧑‍💼 تشخيص المستشار:
            </div>
            <ul style={{ margin: 0, paddingInlineStart: 18, fontSize: 13, lineHeight: 1.9, color: "var(--brown-deep)" }}>
              {a.overallSig.p > 0.10 && (
                <li>📊 معظم النتائج <b>غير ذات دلالة إحصائية</b> — حملاتك قد تكون تحت تأثير عوامل أخرى. زِد حجم العينة قبل الحكم.</li>
              )}
              {a.rlr >= 3 && (
                <li>🎯 معدل Revenue Lift ممتاز (<b>{fm(a.rlr, 2)}×</b>) — كرّر الحملات الأعلى أداءً وضاعف ميزانيتها.</li>
              )}
              {a.rlr > 0 && a.rlr < 1 && (
                <li>⛔ Revenue Lift &lt; 1 — كل ريال تنفقه يُعيد أقل من ريال. أوقف الحملات الخاسرة فوراً.</li>
              )}
              {a.cic > 0 && a.cic < 40 && (
                <li>✨ تكلفة العميل الجديد ممتازة (<b>{fm(a.cic, 1)} ر</b>) — مقارنة مع متوسط 80-120 ر في القطاع.</li>
              )}
              {a.cic >= 100 && (
                <li>💸 تكلفة العميل الجديد مرتفعة (<b>{fm(a.cic, 1)} ر</b>) — راجع قنوات الاكتساب، ركّز على الإحالة (الشركاء) بدل الخصم المباشر.</li>
              )}
              {a.cannibalRisk && (
                <li>🦈 <b>تحذير Cannibalization</b>: نسبة الخصم العالية تأكل مبيعاتك الأصلية. جرّب عرض حجم/إضافة بدل خصم سعر.</li>
              )}
              {a.retRate >= 35 && (
                <li>🔁 معدل العودة (<b>{fm(a.retRate, 1)}%</b>) فوق متوسط القطاع (25%) — قاعدة جيدة لبناء برنامج ولاء.</li>
              )}
              {a.discountPressure > 35 && (
                <li>🎟 ضغط الخصومات مرتفع جداً ({fm(a.discountPressure, 1)}%) — العملاء بدأوا يتعوّدون. استبدل بـ«قيمة مضافة» (مشروب مع زيادة شراب مجاناً).</li>
              )}
              {a.pos / a.totalCamps < 0.5 && a.totalCamps >= 3 && (
                <li>🏥 أكثر من نصف حملاتك خاسرة — اعمل Post-Mortem لكل حملة سابقة قبل إطلاق الجديدة.</li>
              )}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
